import * as v from 'valibot';

import { ENOPOrganType, ESeverity } from '#shared';

import { URGENCY_BAND_ORDER } from '#entities/CaseRanking';
import type { ProcessOwner } from '#entities/CaseTimeline';

import { CLINICAL_EVENTS_DISPLAY_OPTIONS } from '../model';

export type ControlKind =
  | 'severity'
  | 'ageSteps'
  | 'slipTolerance'
  | 'display'
  | 'windowMultiplier'
  | 'toggle'
  | 'none';

export interface ValueContract {
  schema: v.GenericSchema;
  rangeText: string;
  control: ControlKind;
  options: readonly string[];
}

export const EVENT_SEVERITY_PREFIX = 'eventSeverity.';

const SEVERITY_OPTIONS: readonly string[] = Object.values(ESeverity);

const severityContract: ValueContract = {
  schema: v.enum(ESeverity),
  rangeText: `One of ${SEVERITY_OPTIONS.join(', ')}`,
  control: 'severity',
  options: SEVERITY_OPTIONS,
};

const minutes = v.pipe(v.number(), v.integer(), v.minValue(1));

const ageStepsContract: ValueContract = {
  schema: v.pipe(
    v.object({
      halfFromMinutes: minutes,
      threeQuartersFromMinutes: minutes,
      fullFromMinutes: minutes,
    }),
    v.check(
      ({ halfFromMinutes, threeQuartersFromMinutes, fullFromMinutes }) =>
        halfFromMinutes < threeQuartersFromMinutes &&
        threeQuartersFromMinutes < fullFromMinutes,
    ),
  ),
  rangeText:
    'Three whole numbers of minutes, at least 1 and strictly increasing: half, three quarters, full',
  control: 'ageSteps',
  options: [],
};

const toggleContract: ValueContract = {
  schema: v.boolean(),
  rangeText: 'true or false',
  control: 'toggle',
  options: [],
};

export const MOTION_SETTING_KEYS = {
  newEvent: 'caseTimeline.effect.newEvent',
  p1: 'caseTimeline.effect.p1',
  logisticsComplete: 'caseTimeline.effect.logisticsComplete',
  resort: 'caseTimeline.effect.resort',
} as const;

const toleranceMinutes = v.pipe(v.number(), v.integer(), v.minValue(0));

const slipToleranceSchema = v.object({
  airLeg: toleranceMinutes,
  groundLeg: toleranceMinutes,
  groundStop: toleranceMinutes,
});

export type SettingsSlipTolerance = v.InferOutput<typeof slipToleranceSchema>;

const slipToleranceContract: ValueContract = {
  schema: slipToleranceSchema,
  rangeText:
    'Three whole numbers of minutes, at least 0: air leg, ground leg, ground stop',
  control: 'slipTolerance',
  options: [],
};

export const CASE_TIMELINE_ZONES = [
  'device',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Anchorage',
  'Pacific/Honolulu',
  'UTC',
] as const;

export const CASE_TIMELINE_KINDS = [
  'any',
  'opo',
  'enteredByCustomer',
  'enteredByTmdx',
  'reallocation',
  'trial',
] as const;

export const CASE_TIMELINE_GROUPINGS = [
  'none',
  'aircraft',
  'user',
  'txCenter',
  'donorHospital',
  'organ',
  'opo',
] as const;

export const CASE_TIMELINE_PROCESS_OWNERS = [
  'Aviation',
  'Ground',
  'Clinical',
  'Device/OCS',
  'Finance',
  'Planning and approvals',
  'Resources',
  'Context',
] as const satisfies readonly ProcessOwner[];

export const CASE_TIMELINE_DONOR_TYPES = ['DBD', 'DCD'] as const;

const uniqueArray = <const TItem extends v.GenericSchema>(item: TItem) =>
  v.pipe(
    v.array(item),
    v.check(items => new Set(items).size === items.length),
  );

/** A BE value stored before these filters existed omits them, so each falls back to its code default. */
const optionalFilter = <const TItem extends v.GenericSchema>(item: TItem) =>
  v.optional(uniqueArray(item), () => []);

const caseTimelineViewSchema = v.strictObject({
  filters: v.strictObject({
    organs: v.pipe(
      v.array(v.enum(ENOPOrganType)),
      v.check(organs => new Set(organs).size === organs.length),
    ),
    kind: v.picklist(CASE_TIMELINE_KINDS),
    approvalOutstanding: v.boolean(),
    regions: optionalFilter(v.string()),
    urgencyBands: optionalFilter(v.picklist(URGENCY_BAND_ORDER)),
    waitingOn: optionalFilter(v.picklist(CASE_TIMELINE_PROCESS_OWNERS)),
    donorTypes: optionalFilter(v.picklist(CASE_TIMELINE_DONOR_TYPES)),
    replanning: v.optional(v.boolean(), false),
    anomaly: v.optional(v.boolean(), false),
  }),
  groupBy: v.picklist(CASE_TIMELINE_GROUPINGS),
  lensMs: v.nullable(v.pipe(v.number(), v.integer(), v.minValue(1))),
  zone: v.picklist(CASE_TIMELINE_ZONES),
  details: v.strictObject({ eventDots: v.boolean(), context: v.boolean() }),
});

export type CaseTimelineView = v.InferOutput<typeof caseTimelineViewSchema>;

/** The served view with absent new filters filled from their defaults, or null when it drifts from the mirror. */
export const parseCaseTimelineView = (
  value: unknown,
): CaseTimelineView | null => {
  const result = v.safeParse(caseTimelineViewSchema, value);
  return result.success ? result.output : null;
};

const caseTimelineViewContract: ValueContract = {
  schema: caseTimelineViewSchema,
  rangeText:
    'The per-user Case Timeline view: filters, grouping, lens, zone and details',
  control: 'none',
  options: [],
};

export const CASE_TIMELINE_VIEW_KEY = 'caseTimeline.view';
export const APPROVAL_AGE_STEPS_KEY = 'caseTimeline.approvalAgeSteps';

export const SLIP_TOLERANCE_KEY = 'caseTimeline.slipToleranceMinutes';

const displayContract: ValueContract = {
  schema: v.picklist(CLINICAL_EVENTS_DISPLAY_OPTIONS),
  rangeText: `One of ${CLINICAL_EVENTS_DISPLAY_OPTIONS.join(', ')}`,
  control: 'display',
  options: CLINICAL_EVENTS_DISPLAY_OPTIONS,
};

export const CLINICAL_EVENTS_DISPLAY_KEY = 'caseTimeline.clinicalEventsDisplay';

export const EVENT_CARD_WINDOW_MULTIPLIER_KEY = 'eventCards.windowMultiplier';

export const WINDOW_MULTIPLIER_STEPS: readonly number[] = Array.from(
  { length: 16 },
  (_, index) => (index + 1) * 0.25,
);

const windowMultiplierContract: ValueContract = {
  schema: v.picklist(WINDOW_MULTIPLIER_STEPS as [number, ...number[]]),
  rangeText: 'A multiple of 0.25 from 0.25 to 4',
  control: 'windowMultiplier',
  options: [],
};

const CONTRACTS: readonly { pattern: RegExp; contract: ValueContract }[] = [
  {
    pattern: /^eventSeverity\.[A-Za-z][A-Za-z0-9]*$/,
    contract: severityContract,
  },
  {
    pattern: /^caseTimeline\.approvalAgeSteps$/,
    contract: ageStepsContract,
  },
  {
    pattern: /^caseTimeline\.effect\.[A-Za-z][A-Za-z0-9]*$/,
    contract: toggleContract,
  },
  {
    pattern: /^caseTimeline\.slipToleranceMinutes$/,
    contract: slipToleranceContract,
  },
  {
    pattern: /^caseTimeline\.view$/,
    contract: caseTimelineViewContract,
  },
  {
    pattern: /^caseTimeline\.clinicalEventsDisplay$/,
    contract: displayContract,
  },
  {
    pattern: /^eventCards\.windowMultiplier$/,
    contract: windowMultiplierContract,
  },
];

export const contractFor = (key: string): ValueContract | undefined =>
  CONTRACTS.find(entry => entry.pattern.test(key))?.contract;

export const checkValue = (
  key: string,
  value: unknown,
): 'ok' | 'drift' | 'unmirrored' => {
  const contract = contractFor(key);
  if (!contract) {
    return 'unmirrored';
  }
  return v.is(contract.schema, value) ? 'ok' : 'drift';
};
